import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { AxiosError } from "axios";

import {
  login,
  logout,
  refreshToken,
  verify2FA,
  verifyRecoveryCode,
  selectBranch,
} from "../services/authService";

import { RootState } from "../../store/store";
import {
  onLogin,
  onLogOut,
  onRefreshToken,
  onFailedLoginAttempt,
  setError,
  resetError,
} from "../../store/slices/auth/authSlice";

import { updateUserProfilePicture } from "../../store/slices/userProfile/userProfileSlice";
import { resetSidebarState } from "../../store/slices/sidebar/sidebarSlice";
import { clearRoles } from "../../store/slices/auth/authUserSlice";
import { resetBranchState } from "../../store/slices/branches/branchSlice";
import useModals from "../../hooks/useModals";

/**
 * 🎯 Hook central de autenticación.
 * Maneja login, 2FA, recuperación, refresh y logout.
 */
export const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { openModal } = useModals();

  const auth = useSelector((state: RootState) => state.auth);
  const { error } = useSelector((state: RootState) => state.auth);

  const [errorMessage, setErrorMessage] = useState<{ error?: string }>({});

  // 🧭 Mostrar error temporal
  const showError = (message: { error?: string }) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage({}), 4000);
  };

  /**
   * 🔹 Login normal (sin menús ni extras)
   */
  const handleLogin = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    try {
      const response = await login({ email, password });

      // Si requiere 2FA
      if (response.data.requires2FA) {
        navigate("/auth/two-step-verification", { state: { email } });
        return;
      }

      // Si el usuario tiene más de 1 sucursal asignada, debe elegir con cuál entrar
      if (response.data.requiresBranchSelection) {
        navigate("/auth/select-branch", {
          state: { email, branches: response.data.branches || [] },
        });
        return;
      }

      const { accessToken, refreshToken } = response.data;
      const claims = JSON.parse(window.atob(accessToken.split(".")[1]));

      const authData = {
        email: claims.email,
        roles: claims.roles || [],
        permissions: claims.permissions || [],
        accessToken,
        refreshToken,
        iat: claims.iat,
        exp: claims.exp,
        isAuthenticated: true,
      };

      sessionStorage.setItem("auth", JSON.stringify(authData));
      dispatch(onLogin(authData));

      openModal("welcome", { username: email });
      navigate("/");
    } catch (error) {
      handleAuthError(error);
    }
  };

  /**
   * 🔹 Verificación de código 2FA
   */
  const handleVerify2FA = async ({
    email,
    code,
  }: {
    email: string;
    code: string;
  }) => {
    try {
      const response = await verify2FA(email, code);

      // Si el usuario tiene más de 1 sucursal asignada, debe elegir con cuál entrar
      if (response.data.requiresBranchSelection) {
        navigate("/auth/select-branch", {
          state: { email, branches: response.data.branches || [] },
        });
        return;
      }

      const { accessToken, refreshToken, roles, permissions } = response.data;
      const claims = JSON.parse(window.atob(accessToken.split(".")[1]));

      const authData = {
        email: claims.email,
        roles: roles || [],
        permissions: permissions || [],
        accessToken,
        refreshToken,
        iat: claims.iat,
        exp: claims.exp,
        isAuthenticated: true,
      };

      sessionStorage.setItem("auth", JSON.stringify(authData));
      dispatch(onLogin(authData));

      openModal("welcome", { username: email });
      navigate("/");
    } catch (error: any) {
      dispatch(setError(error?.response?.data?.message));
    }
  };

  /**
   * 🔹 Verificación de código de recuperación
   */
  const handleVerifyRecoveryCode = async ({
    email,
    code,
  }: {
    email: string;
    code: string;
  }) => {
    try {
      const response = await verifyRecoveryCode(email, code);

      // Si el usuario tiene más de 1 sucursal asignada, debe elegir con cuál entrar
      if (response.data.requiresBranchSelection) {
        navigate("/auth/select-branch", {
          state: { email, branches: response.data.branches || [] },
        });
        return;
      }

      const { accessToken, refreshToken, roles, permissions } = response.data;
      const claims = JSON.parse(window.atob(accessToken.split(".")[1]));

      const authData = {
        email: claims.email,
        roles: roles || [],
        permissions: permissions || [],
        accessToken,
        refreshToken,
        iat: claims.iat,
        exp: claims.exp,
        isAuthenticated: true,
      };

      sessionStorage.setItem("auth", JSON.stringify(authData));
      dispatch(onLogin(authData));

      openModal("welcome", { username: email });
      navigate("/");
    } catch (error: any) {
      dispatch(setError(error?.response?.data?.message));
    }
  };

  /**
   * 🔹 Selección de sucursal (usuarios con más de 1 sucursal asignada)
   */
  const handleSelectBranch = async ({
    email,
    branchId,
  }: {
    email: string;
    branchId: number;
  }) => {
    try {
      const response = await selectBranch(email, branchId);
      const { accessToken, refreshToken, roles, permissions } = response.data;
      const claims = JSON.parse(window.atob(accessToken.split(".")[1]));

      const authData = {
        email: claims.email,
        roles: roles || [],
        permissions: permissions || [],
        accessToken,
        refreshToken,
        iat: claims.iat,
        exp: claims.exp,
        isAuthenticated: true,
      };

      sessionStorage.setItem("auth", JSON.stringify(authData));
      dispatch(onLogin(authData));

      openModal("welcome", { username: email });
      navigate("/");
    } catch (error: any) {
      dispatch(setError(error?.response?.data?.message));
    }
  };

  /**
   * ⚠️ Manejo de errores de autenticación
   */
  const handleAuthError = (error: unknown) => {
    const axiosError = error as AxiosError;

    if (axiosError.response?.status === 401) {
      const failedAttempts = auth.failedLoginAttempts + 1;
      dispatch(onFailedLoginAttempt());

      if (failedAttempts >= 5) {
        setErrorMessage({ error: "Tu cuenta ha sido bloqueada por seguridad." });
        navigate("/locked/user");
      } else if (failedAttempts === 4) {
        setErrorMessage({
          error: "¡Advertencia! Un intento más y tu cuenta será bloqueada.",
        });
      } else {
        setErrorMessage({ error: "Correo o contraseña incorrectos." });
      }
    } else if (axiosError.response?.status === 403) {
      setErrorMessage({ error: "No tienes acceso al recurso." });
    } else {
      console.error("Error desconocido:", error);
      setErrorMessage({
        error: "Ocurrió un error inesperado. Por favor, inténtalo de nuevo.",
      });
    }
  };

  /**
   * ♻️ Renovación automática del token
   */
  const handleTokenRefresh = async () => {
    const authData = JSON.parse(sessionStorage.getItem("auth") || "{}");
    const storedRefreshToken = authData.refreshToken;
    const accessToken = authData.accessToken;

    if (storedRefreshToken && accessToken) {
      try {
        const response = await refreshToken(storedRefreshToken);

        if (response && response.status === 200) {
          const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
            response.data;

          const claims = JSON.parse(window.atob(newAccessToken.split(".")[1]));
          const updatedAuthData = {
            ...authData,
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
            exp: claims.exp,
          };

          sessionStorage.setItem("auth", JSON.stringify(updatedAuthData));

          dispatch(
            onRefreshToken({
              accessToken: newAccessToken,
              exp: claims.exp,
            })
          );
        } else {
          onLogout();
        }
      } catch (error) {
        console.error("Error al refrescar el token:", error);
        onLogout();
      }
    } else {
      onLogout();
    }
  };

  /**
   * 🚪 Cerrar sesión
   */
  const onLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }

    dispatch(onLogOut());
    dispatch(updateUserProfilePicture(null));
    // El sidebar (perfil + módulos + menús por módulo) es por usuario: hay que
    // limpiarlo al salir para que el siguiente usuario no herede el menú anterior.
    dispatch(resetSidebarState());
    // Mismo problema con los roles de `useAuthUser` (state.authUsers): sin
    // esto, `hasLoaded` queda en true y AppRoute nunca vuelve a pedirlos, así
    // que el siguiente usuario (login SPA, sin F5) hereda los roles del
    // anterior en memoria — p. ej. un mesero "veía" el selector de Mesero
    // porque el ADMIN/SUPPORT de la sesión previa sí puede elegirlo.
    dispatch(clearRoles());
    dispatch(resetBranchState());
    sessionStorage.clear();
    navigate("/login");
  };

  const handleResetError = () => {
    dispatch(resetError());
  };

  const getAuthenticatedUserEmail = (): string | null => {
    const authData = JSON.parse(sessionStorage.getItem("auth") || "{}");
    return authData?.email || null;
  };

  return {
    auth,
    handleLogin,
    handleVerify2FA,
    handleVerifyRecoveryCode,
    handleSelectBranch,
    handleTokenRefresh,
    onLogout,
    errorMessage,
    error,
    handleResetError,
    getAuthenticatedUserEmail,
    showError,
  };
};
